import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import { Check } from 'lucide-react';
import Modal from 'ui/Modal';
import ProfileSettingsButton from 'ui/ProfileSettingsButton';
import UserDisplaynameModal from 'components/User/UserDisplaynameModal';
import UserAboutBriefModal from 'components/User/UserAboutBriefModal';
import UserAboutBackgroundModal from 'components/User/UserAboutBackgroundModal';
import UserAboutIssueModal from 'components/User/UserAboutIssueModal';
import UserAlignmentModal from 'components/User/UserAlignmentModal';
import Cookies from 'js-cookie';
import { Progress } from 'ui/ProgressBar';

interface UserOnboardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onTaskUpdated?: () => void;
}

const UserOnboardModal: React.FC<UserOnboardModalProps> = ({ isOpen, onClose, onTaskUpdated }) => {
    const { currentUser, refreshUser, setUserOnboardingTasks } = useUser();
    
    // Modal visibility states
    const [showDisplaynameModal, setShowDisplaynameModal] = useState<boolean>(false);
    const [showBriefModal, setShowBriefModal] = useState<boolean>(false);
    const [showBackgroundModal, setShowBackgroundModal] = useState<boolean>(false);
    const [showIssueModal, setShowIssueModal] = useState<boolean>(false);
    const [showAlignmentModal, setShowAlignmentModal] = useState<boolean>(false);
    
    // Completion status
    const [dontRemind, setDontRemind] = useState<boolean>(false);
    const [nameCompleted, setNameCompleted] = useState<boolean>(false);
    const [briefCompleted, setBriefCompleted] = useState<boolean>(false);
    const [backgroundCompleted, setBackgroundCompleted] = useState<boolean>(false);
    const [issueCompleted, setIssueCompleted] = useState<boolean>(false);
    const [alignmentCompleted, setAlignmentCompleted] = useState<boolean>(false);
    const [completedTasks, setCompletedTasks] = useState<number>(0);
    const totalTasks = 5;

    // Check completion status based on user data
    useEffect(() => {
        if (currentUser) {
            // Display name check
            const hasDisplayName = !!currentUser.display_name && currentUser.display_name.length > 0;
            setNameCompleted(hasDisplayName);
            
            // Brief bio check
            const briefValue = currentUser?.properties?.about_bio_brief?.value;
            const hasBrief = typeof briefValue === 'string' && briefValue.length > 0;
            setBriefCompleted(hasBrief);
            
            // Background check (birthdate, gender, location)
            const hasBirthdate = !!currentUser?.properties?.about_bio_birthdate?.value;
            const hasGender = !!currentUser?.properties?.about_bio_gender?.value;
            const hasLocation = !!currentUser?.properties?.about_bio_location?.value;
            setBackgroundCompleted(hasBirthdate && hasGender && hasLocation);
            
            // Issue check
            const hasIssue = !!currentUser?.properties?.about_bio_issue?.value;
            setIssueCompleted(hasIssue);
            
            // Alignment check (at least 3 answers)
            let alignmentAnswersCount = 0;
            if (currentUser?.properties) {
                alignmentAnswersCount = Object.keys(currentUser.properties)
                    .filter(key => key.startsWith('about_alignment_answer_') && currentUser.properties[key]?.value)
                    .length;
            }
            setAlignmentCompleted(alignmentAnswersCount >= 3);
            
            // Calculate total completed tasks
            let count = 0;
            if (hasDisplayName) count++;
            if (hasBrief) count++;
            if (hasBirthdate && hasGender && hasLocation) count++;
            if (hasIssue) count++;
            if (alignmentAnswersCount >= 3) count++;
            
            // Update the tasks count state
            setCompletedTasks(count);
            
            // Update cookie based on the calculated count, not the state
            // This prevents infinite loop since we're not depending on state
            if (count === totalTasks || dontRemind) {
                Cookies.remove('userOnboardingTasks');
            } else {
                Cookies.set('userOnboardingTasks', count.toString(), { expires: 30 });
                setUserOnboardingTasks(count);
            }
        }
    }, [currentUser, setUserOnboardingTasks, dontRemind]);
    
    // Separate effect to call onTaskUpdated when completedTasks changes
    useEffect(() => {
        if (onTaskUpdated) {
            //console.log('onTaskUpdated', completedTasks);
            onTaskUpdated();
        }
    }, [completedTasks, onTaskUpdated]);

    // Handle modal closings
    const handleModalClose = async () => {
        await refreshUser();
    };

    // Render completion checkmark indicator
    const renderCompletionStatus = (isCompleted: boolean) => {
        if (isCompleted) {
            return (
                <div className="flex items-center justify-center size-6 rounded-full text-green-600">
                    <Check className="size-5" />
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <Modal 
                isOpen={isOpen && !showDisplaynameModal && !showBriefModal && !showBackgroundModal && !showIssueModal && !showAlignmentModal} 
                onClose={onClose} 
                title="About you"
            >
                <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                    <div className="flex flex-col flex-grow space-y-4 w-full">
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            Societea emphasizes bringing context to content and conversations. Share information that helps others better understand you. Set other profile information in your <a className="external-link" href="/settings/profile">settings</a>.
                        </p> 
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            Remember that you can keep most things private, if you want.
                        </p> 
                        <Progress 
                            value={((completedTasks) / totalTasks) * 100}
                            className="relative"
                        >
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-eoe-text-meta dark:text-eoe-dark-text-sub z-10">
                                {completedTasks}/{totalTasks}
                            </span>
                        </Progress>

                        <div className="form-control space-y-2">
                            <ProfileSettingsButton
                                title="Name"
                                description="Set your name; this is usually what people call you."
                                onClick={() => setShowDisplaynameModal(true)}
                                settingValue={renderCompletionStatus(nameCompleted)}
                            />
                        
                            <ProfileSettingsButton
                                title="Brief"
                                description="Add a short bio to your profile."
                                onClick={() => setShowBriefModal(true)}
                                settingValue={renderCompletionStatus(briefCompleted)}
                            />
                            
                            <ProfileSettingsButton
                                title="Background"
                                description="Share basic information about yourself. Provide your age, gender, and location."
                                onClick={() => setShowBackgroundModal(true)}
                                settingValue={renderCompletionStatus(backgroundCompleted)}
                            />
                            
                            <ProfileSettingsButton
                                title="#1 Issue"
                                description="Share what matters most to you."
                                onClick={() => setShowIssueModal(true)}
                                settingValue={renderCompletionStatus(issueCompleted)}
                            />
                            
                            <ProfileSettingsButton
                                title="Perspective"
                                description="Define your social and political views. Respond to at least 3 statements."
                                onClick={() => setShowAlignmentModal(true)}
                                settingValue={renderCompletionStatus(alignmentCompleted)}
                            />
                        </div>
                        <div className="flex flex-row items-center gap-2 justify-start">
                            <span className="flex flex-row justify-start items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm">
                                Don't remind me again
                            </span>
                            <input
                                type="checkbox"
                                checked={dontRemind}
                                disabled={false}
                                onChange={(e) => setDontRemind(!dontRemind)}
                                className="toggle-switch-input col-span-1 flex-shrink-0"
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary w-full sm:w-fit py-2 px-4"
                        >
                            Finish Later
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`w-full sm:w-fit py-2 px-4 ${
                                completedTasks === totalTasks ? 'btn-primary' : 'btn-primary-disabled'
                            }`}
                            disabled={completedTasks < totalTasks}
                        >
                            {completedTasks === totalTasks ? 'Complete' : `${completedTasks}/${totalTasks} Complete`}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Sub-modals */}
            {showDisplaynameModal && (
                <UserDisplaynameModal 
                    onClose={() => {
                        setShowDisplaynameModal(false);
                        handleModalClose();
                    }} 
                />
            )}

            {showBriefModal && (
                <UserAboutBriefModal 
                    onClose={() => {
                        setShowBriefModal(false);
                        handleModalClose();
                    }} 
                />
            )}

            {showBackgroundModal && (
                <UserAboutBackgroundModal 
                    isOpen={showBackgroundModal}
                    onClose={() => {
                        setShowBackgroundModal(false);
                        handleModalClose();
                    }}
                    initialData={currentUser?.properties}
                    privacy={currentUser?.properties?.privacy_content_default_audience?.value || 'public'}
                />
            )}

            {showIssueModal && (
                <UserAboutIssueModal 
                    onClose={() => {
                        setShowIssueModal(false);
                        handleModalClose();
                    }}
                    primaryIssue={currentUser?.properties?.about_bio_issue?.value} 
                />
            )}

            {showAlignmentModal && (
                <UserAlignmentModal 
                    onClose={() => {
                        setShowAlignmentModal(false);
                        handleModalClose();
                    }}
                />
            )}
        </>
    );
};

export default UserOnboardModal;
